import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SearchIcon from '@mui/icons-material/Search';
import ScienceIcon from '@mui/icons-material/Science';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function formatNumber(value) {
  return numberFormatter.format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString();
}

function horseName(record) {
  return record.horse_name || record.inside_horse_name || record.outside_horse_name || '-';
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: soft,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ReproductionPage() {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [semenFilter, setSemenFilter] = useState('all');
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    axios
      .get(`${apiUrl}/reproduction-clinic/recordlist`)
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setRecords(rows.sort((a, b) => new Date(b.insemination_date || 0) - new Date(a.insemination_date || 0)));
      })
      .catch((err) => {
        if (!active) return;
        setError(t('reproduction.loadError') || 'Failed to fetch reproduction records');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [t]);

  const semenTypes = useMemo(
    () =>
      [...new Set(records.map((record) => record.semen_type).filter(Boolean))].sort((a, b) =>
        String(a).localeCompare(String(b))
      ),
    [records]
  );

  const summary = useMemo(() => {
    const mares = new Set(records.map((record) => horseName(record)).filter((name) => name !== '-'));
    const stallions = new Set(records.map((record) => record.stallion_name).filter(Boolean));
    const fresh = records.filter((record) => String(record.semen_type || '').toLowerCase().includes('fresh')).length;
    return {
      total: records.length,
      mares: mares.size,
      stallions: stallions.size,
      fresh,
      lastDate: records[0]?.insemination_date || null,
    };
  }, [records]);

  const filteredRecords = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return records.filter((record) => {
      const semenOk = semenFilter === 'all' || record.semen_type === semenFilter;
      const searchOk =
        !needle ||
        [
          horseName(record),
          record.stallion_name,
          record.semen_type,
          record.semen_source_type,
          record.notes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      return semenOk && searchOk;
    });
  }, [records, search, semenFilter]);

  const pagedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1540, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('reproduction.title') || 'Reproduction'}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {t('reproduction.pageHint') ||
                  'Track mare breeding records, stallions, semen type, and reproductive service history.'}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                component={Link}
                to="/ReproductionProceduresList"
                variant="outlined"
                startIcon={<ScienceIcon />}
                sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 800 }}
              >
                {t('reproduction.procedures') || 'Procedures'}
              </Button>
              <Button
                component={Link}
                to="/DirectInseminationForm"
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
              >
                {t('reproduction.addRecord') || 'Add record'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <MetricCard icon={<FavoriteIcon />} title={t('reproduction.records') || 'Records'} value={formatNumber(summary.total)} helper={t('reproduction.currentData') || 'Current data'} />
          <MetricCard icon={<FavoriteIcon />} title={t('reproduction.mares') || 'Mares'} value={formatNumber(summary.mares)} helper={t('reproduction.uniqueMares') || 'Unique mares'} color={COLORS.rose} soft={COLORS.roseSoft} />
          <MetricCard icon={<ScienceIcon />} title={t('reproduction.stallions') || 'Stallions'} value={formatNumber(summary.stallions)} helper={t('reproduction.uniqueStallions') || 'Unique stallions'} color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<ScienceIcon />} title={t('reproduction.latestRecord') || 'Latest record'} value={formatDate(summary.lastDate)} helper={`${formatNumber(summary.fresh)} ${t('reproduction.freshSemen') || 'fresh semen'}`} color={COLORS.green} soft={COLORS.greenSoft} />
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1.25}
            sx={{ p: 2 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder={t('search') || 'Search'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 320 } }}
              />
              <TextField
                select
                size="small"
                value={semenFilter}
                onChange={(event) => {
                  setSemenFilter(event.target.value);
                  setPage(0);
                }}
                label={t('reproduction.semenType') || 'Semen type'}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="all">{t('show_all') || 'All'}</MenuItem>
                {semenTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Chip
              label={`${formatNumber(filteredRecords.length)} ${t('records') || 'records'}`}
              sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900, alignSelf: { xs: 'flex-start', md: 'center' } }}
            />
          </Stack>

          <TableContainer sx={smartTableContainerSx}>
            <Table size="small" sx={smartTableSx}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell width={70}>#</TableCell>
                  <TableCell width={130}>{t('reproduction.date') || 'Date'}</TableCell>
                  <TableCell>{t('reproduction.horseName') || 'Mare'}</TableCell>
                  <TableCell>{t('reproduction.stallion') || 'Stallion'}</TableCell>
                  <TableCell width={150}>{t('reproduction.semenType') || 'Semen type'}</TableCell>
                  <TableCell width={170}>{t('reproduction.semenSourceType') || 'Source'}</TableCell>
                  <TableCell align="center" width={100}>{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : pagedRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: COLORS.muted }}>
                      {t('no_data') || 'No data'}
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedRecords.map((record, index) => (
                    <TableRow key={record.id} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{formatDate(record.insemination_date)}</TableCell>
                      <TableCell>
                        <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                          {horseName(record)}
                        </Typography>
                      </TableCell>
                      <TableCell>{record.stallion_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={record.semen_type || '-'}
                          sx={{ bgcolor: COLORS.roseSoft, color: COLORS.text, fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={record.semen_source_type || '-'}
                          sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text, fontWeight: 800 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('view') || 'View'}>
                          <IconButton component={Link} to={`/reproduction-details/${record.id}`} sx={{ color: COLORS.blue }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRecords.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 15, 25, 50]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
}
